// Grilla de renglones NOMBRABLES × 24 horas (medicación, infusiones, nebulización, drenajes, "otros"...). Lógica en `filasNombrables.ts`.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { COLUMNAS_HORARIAS_UCI } from './TablaHoraria24h';
import {
  actualizarCeldaFilaNombrable,
  agregarFilaNombrable,
  filaNombrableConDato,
  quitarFilaNombrable,
  renombrarFilaNombrable,
  TOPE_FILAS_NOMBRABLES,
  type EstadoFilasNombrables,
} from './filasNombrables';

export interface FilasNombrablesHorariasProps {
  /** Prefijo de los ids/aria (único por grupo en la pantalla). */
  readonly id: string;
  readonly estado: EstadoFilasNombrables;
  readonly onChange: (estado: EstadoFilasNombrables) => void;
  readonly unidad?: string;
  readonly tope?: number;
}

export function FilasNombrablesHorarias({ id, estado, onChange, unidad, tope = TOPE_FILAS_NOMBRABLES }: FilasNombrablesHorariasProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="border-collapse text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-hce-surface p-1 text-left font-medium text-hce-muted">
                {t('formularios.uci.filasNombrables.nombre')}
                {unidad ? ` (${unidad})` : ''}
              </th>
              {COLUMNAS_HORARIAS_UCI.map((c) => (
                <th key={c} className="p-1 text-center font-medium text-hce-ink">
                  {c}
                </th>
              ))}
              <th className="p-1"><span className="sr-only">{t('formularios.uci.filasNombrables.acciones')}</span></th>
            </tr>
          </thead>
          <tbody>
            {estado.map((fila, i) => (
              <tr key={i} className="border-t border-border">
                <td className="sticky left-0 z-10 bg-hce-surface p-0.5">
                  <Input
                    id={`${id}-nombre-${i + 1}`}
                    className="h-7 w-48 text-xs"
                    value={fila.nombre}
                    aria-label={t('formularios.uci.filasNombrables.nombreFila', { n: i + 1 })}
                    onChange={(e) => onChange(renombrarFilaNombrable(estado, i, e.target.value))}
                  />
                </td>
                {COLUMNAS_HORARIAS_UCI.map((c) => (
                  <td key={c} className="p-0.5">
                    <Input className="h-7 w-14 text-xs" value={fila.horas[c] ?? ''} aria-label={t('formularios.uci.filasNombrables.celda', { n: i + 1, nombre: fila.nombre.trim(), hora: c })} onChange={(e) => onChange(actualizarCeldaFilaNombrable(estado, i, c, e.target.value))} />
                  </td>
                ))}
                <td className="p-0.5">
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" aria-label={t('formularios.uci.filasNombrables.quitar', { n: i + 1 })} onClick={() => {
                      // Un renglón con datos (hasta 24 celdas) no se borra de un clic accidental.
                      if (filaNombrableConDato(fila) && !window.confirm(t('formularios.uci.filasNombrables.confirmarQuitar', { n: i + 1 }))) return;
                      onChange(quitarFilaNombrable(estado, i));
                    }}>
                    ×
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="w-fit" disabled={estado.length >= tope} onClick={() => onChange(agregarFilaNombrable(estado, tope))}>
          {t('formularios.uci.filasNombrables.agregar')}
        </Button>
        {estado.length >= tope ? <span className="text-xs text-hce-muted">{t('formularios.uci.filasNombrables.tope', { max: tope })}</span> : null}
      </div>
    </div>
  );
}
